import React, { useContext } from 'react';
import styled from 'styled-components/macro';
import { assertNotNull } from '../../../../../helpers/nullHelpers';
import { FieldContainer } from '../../../../../shared/form/FormField';
import { SelectField } from '../../../../../shared/form/inputs/SelectField';
import { TextInputField } from '../../../../../shared/form/inputs/TextInputField';
import { ReadOnlyFieldContainer } from '../../../../../shared/form/ReadOnlyField';
import { ManifoldFormModel } from '../../manifolds/manifoldFormModel';
import { assertIsQuoteFormContextValue, QuoteOrDesignFormContext } from '../../QuoteOrDesignFormContext';
import {
  floorLevelSelectOptions,
  getQuoteRoomThermostatSelectOptions,
  getThermostatSharedWithOptions,
} from '../../quoteOrDesignFormData';
import { roomFieldName } from '../../quoteOrDesignFormFieldNameHelpers';
import { RoomFormModel } from '../roomFormModel';
import { RoomAutocompleteItem, RoomType, roomTypeSelectOptions } from '../roomTypes';
import { RoomFloorConstructionField } from './RoomFloorConstructionField';
import { RoomNameField } from './RoomNameField';
import { RoomNumberOfPortsField } from './RoomNumberOfPortsField';
import { RoomPerimeterField } from './RoomPerimeterField';

type Props = {
  unitIndex: number;
  manifoldIndex: number;
  manifold: ManifoldFormModel;
  roomIndex: number;
  room: RoomFormModel;
  handleSelectionOfRoomName: (selectedItem: RoomAutocompleteItem) => void;
  handleSelectionOfRoomType: (selectedType: RoomType) => void;
};

export const QuoteRoomFields = ({
  unitIndex,
  manifoldIndex,
  manifold,
  roomIndex,
  room,
  handleSelectionOfRoomName,
  handleSelectionOfRoomType,
}: Props) => {
  const hasSharedThermostat = room.thermostatTypeCode === 'Shared';

  const formContext = assertNotNull(useContext(QuoteOrDesignFormContext));
  assertIsQuoteFormContextValue(formContext);

  return (
    <RoomFieldsContainer>
      <RoomFieldContainer $width="85px">
        <SelectField
          name={roomFieldName(unitIndex, manifoldIndex, roomIndex, 'floorLevelCode')}
          label="Level"
          options={floorLevelSelectOptions}
        />
      </RoomFieldContainer>
      <RoomFieldContainer $width="140px">
        <RoomNameField
          unitIndex={unitIndex}
          manifoldIndex={manifoldIndex}
          roomIndex={roomIndex}
          handleSelectionOfRoomName={handleSelectionOfRoomName}
        />
      </RoomFieldContainer>
      <RoomFieldContainer $width="140px">
        <SelectField
          name={roomFieldName(unitIndex, manifoldIndex, roomIndex, 'roomType')}
          label="Room type"
          options={roomTypeSelectOptions}
          onChange={handleSelectionOfRoomType}
        />
      </RoomFieldContainer>
      <RoomFieldContainer $width="100px">
        <TextInputField
          name={roomFieldName(unitIndex, manifoldIndex, roomIndex, 'roomTemperature')}
          label="Temp. (°C)"
        />
      </RoomFieldContainer>
      <RoomFieldContainer $width="130px">
        <RoomFloorConstructionField
          unitIndex={unitIndex}
          manifoldIndex={manifoldIndex}
          roomIndex={roomIndex}
          isWithinRadiatorManifold={manifold.isRadiator}
        />
      </RoomFieldContainer>
      <RoomFieldContainer $width="110px">
        <RoomPerimeterField unitIndex={unitIndex} manifoldIndex={manifoldIndex} roomIndex={roomIndex} room={room} />
      </RoomFieldContainer>
      <RoomFieldContainer $width="110px">
        <TextInputField
          name={roomFieldName(unitIndex, manifoldIndex, roomIndex, 'areaInMetresSquared')}
          label={
            <>
              Area (m<sup>2</sup>)
            </>
          }
        />
      </RoomFieldContainer>
      <RoomFieldContainer $width="70px">
        <RoomNumberOfPortsField
          unitIndex={unitIndex}
          manifoldIndex={manifoldIndex}
          manifold={manifold}
          roomIndex={roomIndex}
          isWithinRadiatorManifold={manifold.isRadiator}
          room={room}
        />
      </RoomFieldContainer>
      <RoomFieldContainer $width="130px">
        <SelectField
          name={roomFieldName(unitIndex, manifoldIndex, roomIndex, 'thermostatTypeCode')}
          label="Thermostat"
          options={getQuoteRoomThermostatSelectOptions(
            formContext.formProps.values.thermostatTypeCode,
            room.thermostatTypeCode,
          )}
        />
      </RoomFieldContainer>
      <RoomFieldContainer $width="140px">
        <SelectField
          name={roomFieldName(unitIndex, manifoldIndex, roomIndex, 'thermostatSharedWithId')}
          label="Shared With"
          options={getThermostatSharedWithOptions(manifold)}
          disabled={!hasSharedThermostat}
        />
      </RoomFieldContainer>
      {!manifold.isRadiator && (
        <>
          <RoomFieldContainer $width="115px">
            <TextInputField
              name={roomFieldName(unitIndex, manifoldIndex, roomIndex, 'numberOfThermostats')}
              label="Thermostats"
              disabled={hasSharedThermostat}
            />
          </RoomFieldContainer>
          <RoomFieldContainer $width="120px">
            <TextInputField
              name={roomFieldName(unitIndex, manifoldIndex, roomIndex, 'numberOfWetSensors')}
              label="Wet Sensors"
              disabled={hasSharedThermostat}
            />
          </RoomFieldContainer>
          <RoomFieldContainer $width="135px">
            <TextInputField
              name={roomFieldName(unitIndex, manifoldIndex, roomIndex, 'numberOfFloorSensors')}
              label="Floor Sensors"
              disabled={hasSharedThermostat}
            />
          </RoomFieldContainer>
        </>
      )}
    </RoomFieldsContainer>
  );
};

const RoomFieldsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  flex-direction: row;
  justify-content: flex-start;

  ${FieldContainer}, ${ReadOnlyFieldContainer} {
    margin: 8px 2px 4px 2px !important;
  }
`;

const RoomFieldContainer = styled.div<{ $width: string }>`
  flex: 1;
  min-width: ${props => props.$width};
  max-width: 200px;

  &:empty {
    display: none;
  }
`;
